import "../components/monthly-theme-panel/MonthlyThemePanel";

import { CCCMonthlyThemePanel } from "../components/monthly-theme-panel/MonthlyThemePanel";
import { MonthlyThemePage } from "../core/interfaces/MonthlyTheme";
import SelectItem from "../core/interfaces/SelectItem";
import { DateFormat, formatDate, getDomElement } from "../core/utilities";
import MonthlyThemeModel from "../models/MonthlyThemeModel";

interface MonthlyDeferredPage {
  page: MonthlyThemePage;
  month: SelectItem;
}
export class MonthlyThemePanelController {
  $container: HTMLElement = null;
  $panel: CCCMonthlyThemePanel = null;
  minMonths = 3;

  init(anchor: string): void {
    this.$container = getDomElement(anchor);
    if (this.$container) {
      this.addMonthlyThemePanel();
    }
  }

  async addMonthlyThemePanel(): Promise<void> {
    this.$panel = document.createElement(
      "ccc-monthly-theme-panel"
    ) as CCCMonthlyThemePanel;
    this.$container.append(this.$panel);
    const startDate = new Date();
    // Starts 2 years from now.
    startDate.setMonth(startDate.getMonth() - 24);
    const urlParams = new URLSearchParams(window.location.search);
    const isExternal =
      urlParams.get("oracle") === "true" || urlParams.get("Oracle") === "true";
    const activeTab = isExternal
      ? this.$panel.viewModel.viewOptions[1]
      : this.$panel.viewModel.viewOptions[0];
    this.$panel.$viewSelect.setValue(activeTab);
    await this.checkDataForPeriod(activeTab.name, startDate);
    this.setListenerForTabChange(startDate);
  }

  async checkDataForPeriod(activeTab: string, startDate: Date) {
    if (activeTab === this.$panel.viewModel.viewOptions[0].name) {
      await this.checkDataForMonths(
        this.determineMonths(
          this.$container.getAttribute("start-time") || startDate.toISOString()
        )
      );
      this.$panel.monthChangeFnc = this.handleMonthChange.bind(this);
    } else {
      await this.checkDataForQuarters(
        this.determineQuarters(
          this.$container.getAttribute("start-time") || startDate.toISOString()
        )
      );
      this.$panel.monthChangeFnc = this.handleMonthChange.bind(this);
    }
  }

  setListenerForTabChange(startDate: Date) {
    this.$panel.$viewSelect.onItemSelect(async (selectedItem: SelectItem) => {
      const urlParams = new URLSearchParams(window.location.search);
      if (selectedItem.name === "Oracle Employees") {
        urlParams.set("oracle", "true");
      } else {
        urlParams.delete("oracle");
      }
      //Make new URL and update without refreshing,
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState(null, "", newUrl);

      if (selectedItem.name === this.$panel.viewModel.viewOptions[0].name) {
        await this.checkDataForMonths(
          this.determineMonths(
            this.$container.getAttribute("start-time") ||
              startDate.toISOString()
          )
        );
      } else if (
        selectedItem.name === this.$panel.viewModel.viewOptions[1].name
      ) {
        await this.checkDataForQuarters(
          this.determineQuarters(
            this.$container.getAttribute("start-time") ||
              startDate.toISOString()
          )
        );
      }
    });
  }

  determineMonths(initDate: string): Array<SelectItem> {
    const today = new Date(Date.now());

    const startDate = new Date(initDate);
    const months: Array<SelectItem> = [];

    let idx = 1;

    while (
      startDate.getFullYear() < today.getFullYear() ||
      (startDate.getFullYear() === today.getFullYear() &&
        startDate.getMonth() <= today.getMonth())
    ) {
      months.push({
        id: idx,
        name: `${formatDate(startDate, DateFormat.month)}, ${formatDate(
          startDate,
          DateFormat.year
        )}`,
      });

      idx++;
      startDate.setMonth(startDate.getMonth() + 1);
    }
    if (months.length > 0) {
      months.reverse();
    }
    console.debug("Monthly Theme - Months:", months);

    return months;
  }

  determineQuarters(initDate: string): Array<SelectItem> {
    const today = new Date();
    const startDate = new Date(initDate);
    const quarters: Array<SelectItem> = [];
    let idx = 1;
    const quarterMonths = [2, 5, 8, 11];

    while (startDate <= today) {
      const month = startDate.getMonth();
      if (quarterMonths.includes(month)) {
        quarters.push({
          id: idx,
          name: `${formatDate(
            startDate,
            DateFormat.month
          )}, ${startDate.getFullYear()}`,
        });
        idx++;
      }
      startDate.setMonth(startDate.getMonth() + 1);
    }
    return quarters.reverse();
  }

  async checkDataForMonths(months: Array<SelectItem>): Promise<void> {
    console.debug("Monthly Theme - Checking data for given months");
    this.$panel.viewModel.months([]);
    const monthPromises: Array<Promise<MonthlyDeferredPage>> = [];
    for (let i = 0; i <= months.length - 1; i++) {
      console.debug("Monthly Theme - Requesting for a month page:", months[i]);
      console.debug("Monthly Theme - Wait for response");
      monthPromises.push(this.getDeferredPage(months[i]));
    }

    Promise.all(monthPromises)
      .then((responses) => {
        responses.forEach((res) => {
          this.checkPage(res.page, res.month);
        });
      })
      .finally(() => {
        this.selectDefaultPage();
        this.$panel.viewModel.ready(true);
      });
  }

  async checkDataForQuarters(quarters: Array<SelectItem>): Promise<void> {
    console.debug("Monthly Theme - Checking data for given quarters");
    this.$panel.viewModel.months([]);
    const quarterPromises: Array<Promise<MonthlyDeferredPage>> = [];
    for (let i = 0; i <= quarters.length - 1; i++) {
      quarterPromises.push(this.getDeferredPage(quarters[i]));
    }

    Promise.all(quarterPromises)
      .then((responses) => {
        responses.forEach((res) => {
          this.checkPage(res.page, res.month);
        });
      })
      .finally(() => {
        this.selectDefaultPage();
        this.$panel.viewModel.ready(true);
      });
  }

  selectDefaultPage(): void {
    if (this.$panel.viewModel.months().length > 0) {
      this.$panel.monthSelector.setValue(this.$panel.viewModel.months()[0]);
    } else {
      console.debug("Monthly Theme - no months  found");
    }
  }

  checkPage(page: MonthlyThemePage | null, month: SelectItem): void {
    if (page !== null) {
      console.debug("Monthly Theme - Found:", month);
      this.$panel.viewModel.months().push(month);
    } else {
      console.debug("Monthly Theme - Not found:", month);
    }
  }

  async getDeferredPage(month: SelectItem): Promise<MonthlyDeferredPage> {
    const page = await this.getPage(month);

    return {
      page: page,
      month: month,
    };
  }

  async getPage(month: SelectItem): Promise<MonthlyThemePage> {
    let pageName: string;
    try {
      if (
        this.$panel.$viewSelect.getValue().name ===
        this.$panel.viewModel.viewOptions[0].name
      ) {
        pageName = month.name.toLowerCase().replace(", ", "-");
      } else {
        pageName = month.name.toLowerCase().replace(", ", "-quarterly-");
      }
      return await MonthlyThemeModel.getPage(pageName);
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  async handleMonthChange(): Promise<void> {
    const page = await this.getPage(this.$panel.viewModel.activeItem());
    const sanitizedPage = this.sanitizePage(page);
    this.$panel.viewModel.activePage(sanitizedPage);
  }

  /**
   * handle different monthly theme item formats
   */
  sanitizePage(page: MonthlyThemePage): MonthlyThemePage {
    const items = page.items.map((item) => {
      return {
        name: item.name,
        level: item.description || item.level,
        avatar: item.iconUrl || item.avatar,
        link: item.to || item.link,
        userId: item.userId,
        count: item.counts,
      };
    });
    const newPage = { ...page, items };
    return newPage;
  }
}
